import React from 'react';
import PageHero from '../components/PageHero';
import { Helmet } from 'react-helmet-async';
import CTASection from '../components/CTASection';
import { SERVICE_MYO } from '../assets/images';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Myofunctional from '../assets/Hero/Myofunctional.svg';
const { FiCheck, FiActivity } = FiIcons;

export default function MyofunctionalTherapy() {
  const benefits = [
    { title: "Breathing Improvement", desc: "Promotes healthy nasal breathing." },
    { title: "Sleep Quality", desc: "Reduces snoring and sleep disruptions." },
    { title: "Jaw Development", desc: "Guides proper facial and structural growth." },
    { title: "Swallowing Function", desc: "Corrects improper swallowing patterns." }
  ];

  const steps = [
    "Comprehensive Evaluation",
    "Customized Exercise Plan",
    "Regular Therapy Sessions",
    "Progress Tracking & Maintenance"
  ];

  return (
    <div>

      {/* ✅ Helmet Added */}
      <Helmet>
        <title>Myofunctional Therapy | Improve Breathing & Oral Function</title>
        <meta
          name="description"
          content="Myofunctional therapy to correct tongue posture, improve breathing, reduce snoring, and support proper jaw development in children and adults."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalTherapy",
            "name": "Myofunctional Therapy",
            "description": "Therapy focused on improving tongue posture, breathing, and oral muscle function.",
            "procedureType": "Non-invasive"
          })}
        </script>
      </Helmet>
      
      <PageHero
        title="Myofunctional Therapy"
        subtitle="Restore proper tongue posture and facial muscle function to improve breathing, sleep quality, and overall health."
        image={Myofunctional}
      />

      {/* What is it */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Text */}
          <div>
            <h2 className="text-3xl font-bold text-secondary mb-6">
              What is Myofunctional Therapy?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Orofacial Myofunctional Therapy (OMT) is a program used to correct the improper function of the tongue and facial muscles used at rest, for swallowing, and speaking. It's essentially "physical therapy" for the muscles of your mouth and face, aiming to establish optimal breathing and oral posture.
            </p>
          </div>

          {/* Image */}
          <div>
            <img
              src={Myofunctional}
              alt="Myofunctional Therapy"
              className="w-full rounded-xl shadow-soft"
            />
          </div>

        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-16 text-center">Key Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white p-8 rounded-xl shadow-soft text-center">
                <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={FiActivity} className="text-primary text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-secondary mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process & Who Needs It */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-3xl font-bold text-secondary mb-8">Who Needs It?</h2>
            <p className="text-lg text-gray-600 mb-6">
              Therapy is beneficial for both children and adults who experience:
            </p>
            <ul className="space-y-4">
              {['Mouth breathing', 'Snoring or sleep apnea', 'Tongue thrust', 'Speech articulation issues', 'Orthodontic relapse', 'TMJ disorders'].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-700">
                  <SafeIcon icon={FiCheck} className="text-primary" />
                  <span className="text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-secondary text-white p-10 rounded-xl">
            <h2 className="text-3xl font-bold mb-8">The Process</h2>
            <div className="space-y-8">
              {steps.map((step, idx) => (
                <div key={idx} className="flex items-center gap-6">
                  <div className="w-12 h-12 rounded-full bg-accent2 flex items-center justify-center font-bold text-xl flex-shrink-0">
                    {idx + 1}
                  </div>
                  <span className="text-xl font-medium">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}